import type { HcState } from "./HcState";
import { getHcStorage, setHcStorage } from "./useHcStorage";

/**
 * Get the default start date of the session(?), which is apparently
 * tomorrow's date, according to `@hc-frontend/shared-assets`.
 */
function startDate(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const monthString = `${tomorrow.getMonth() + 1}`;
  const month = `0${monthString}`.substring(monthString.length - 1);
  return `${month}/${tomorrow.getDate()}/${tomorrow.getFullYear()}`;
}

/**
 * Overlay a persisted `hc` object on top of the defaults, ignoring keys that are
 * absent or explicitly null.
 *
 * The object in `localStorage` is untyped at runtime and is shared with older
 * Healthcare.com pages, so it can be missing fields that the current `HcState`
 * declares. Consumers read those fields without guarding — `useHc` calls
 * `hc.pubid.toString()`, for example — so seeding the store straight from a
 * partial object throws inside `useHc`'s effect and the session is never
 * established.
 */
function withDefaults(stored: Partial<HcState>): HcState {
  const defined = Object.fromEntries(
    Object.entries(stored).filter(
      ([, value]) => value !== null && value !== undefined
    )
  ) as Partial<HcState>;

  return { ...defaultHcState(), ...defined };
}

/**
 * Set the default state of the `hc` object in `localStorage`, as originally defined in
 * `@hc-frontend/shared-assets`, and return it. If there's already an `hc` object in
 * `localStorage`, return that merged over the defaults, so that a partially
 * populated or outdated stored object never reaches the store with holes in it.
 */
export default function initialState(): HcState {
  // localStorage is not available in Node.js, e.g. when we are running `nx build dtc`,
  // so we should only touch it when we know we're running in a browser-like environment.
  const inBrowser = typeof window !== "undefined";
  /* c8 ignore next */
  const storedState = inBrowser ? getHcStorage() : null;
  if (storedState) return withDefaults(storedState);

  const state = defaultHcState();

  if (inBrowser) setHcStorage(state);

  return state;
}

/**
 * The default `hc` object, as originally defined in `@hc-frontend/shared-assets`.
 * Pure — does not read or write `localStorage`.
 */
export function defaultHcState(): HcState {
  return {
    location: {
      zip_code: "",
      state_acronym: "",
      state: "",
      county: "",
      city: "",
    },
    cache: {
      locationByZip: {},
    },
    household: {
      size: 1,
      income: "",
      fpl: "",
    },
    contact: {
      first_name: "",
      last_name: "",
      email_address: "",
      address: "",
      phone_home: "",
      is_mobile: "",
    },
    members: [],
    life_event: 0,
    leadid: {
      universal_leadid: "",
      sended: false,
    },
    invoca_phone: {
      promo_number: "",
      promo_number_formatted: "",
    },
    sales_center_availability: {
      sunday: {
        to: "",
        from: "",
      },
      monday: {
        to: "",
        from: "",
      },
      tuesday: {
        to: "",
        from: "",
      },
      wednesday: {
        to: "",
        from: "",
      },
      thursday: {
        to: "",
        from: "",
      },
      friday: {
        to: "",
        from: "",
      },
      saturday: {
        to: "",
        from: "",
      },
    },
    utm: {
      utm_source: "",
      utm_campaign: "",
      utm_medium: "",
      utm_content: "",
      utm_term: "",
    },
    aid: "",
    pubid: 0,
    subid: 0,
    gclid: "",
    msclkid: "",
    irpid: "",
    ircid: "",
    bt_sid: "",
    source: "",
    pixel: "",
    url_version: "",
    landing_url: "",
    picreel_time: 0,
    healthcareSession: "",
    healthcareUserId: "",
    flowCatch: { show: false },
    agent_id: "",
    /* c8 ignore next */
    version: "latest",
    period: "364",
    sdate: startDate(),
    lastUpdated: Date.now(),
  };
}
